/**
 * Military base layer renderer -- matching Worldmonitor 1:1.
 * Uses IconLayer with triangleUp shape (mask:true via white SVG atlas),
 * colored by country/allegiance type (US-NATO blue, Russia red, ...).
 * Below zoom 5 the dense base fields collapse into faction-colored cluster
 * discs with a centered count (WM DeckGLMap.ts:2966-2994 -- WM's clusters
 * are server-side; Sentinel builds the same sign with a local Supercluster).
 *
 * MIT License -- see THIRD-PARTY-NOTICES.md
 */

import Supercluster from 'supercluster';
import { IconLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import { MILITARY_BASES } from '@/config/data';
import type { MilitaryBase, MilitaryBaseType } from '@/config/data';
import { MARKER_ICONS, BASES_ICON_MAPPING } from '@/map/layer-colors';

/** Layer IDs handled by this renderer. */
export const MILITARY_BASE_LAYER_IDS = new Set(['military-bases']);

/** Zoom below which bases render as clusters instead of individual icons. */
const CLUSTER_MAX_ZOOM = 5;

/**
 * Country/allegiance type -> RGBA color mapping (matching Worldmonitor getBaseColor).
 * WM reference: DeckGLMap.ts createBasesLayer() / getBaseColor()
 */
function getBaseColor(type: MilitaryBaseType, alpha: number): [number, number, number, number] {
  switch (type) {
    case 'us-nato':  return [68, 136, 255, alpha];
    case 'russia':   return [255, 68, 68, alpha];
    case 'china':    return [255, 136, 68, alpha];
    case 'uk':       return [68, 170, 255, alpha];
    case 'france':   return [0, 85, 164, alpha];
    case 'india':    return [255, 153, 51, alpha];
    case 'japan':    return [188, 0, 45, alpha];
    case 'italy':    return [0, 146, 70, alpha];
    case 'uae':      return [200, 160, 80, alpha];
    case 'turkey':   return [227, 10, 23, alpha];
    case 'other':    return [136, 136, 136, alpha];
    default:         return [136, 136, 136, alpha];
  }
}

// ---------------------------------------------------------------------------
// Static cluster index (built lazily once -- the base set never changes)
// ---------------------------------------------------------------------------

export interface BaseCluster {
  lng: number;
  lat: number;
  count: number;
  dominantType: MilitaryBaseType;
}

// No map/reduce: Supercluster's reduce accumulator is a SHALLOW clone of
// nested props, so mutating a nested counts object writes neighbor counts
// into child clusters' stored props (empirically corrupts dominant types).
// The base set is ~230 rows, so counting via getLeaves is cheap and safe.
let baseIndex: Supercluster<{ base: MilitaryBase }> | null = null;

function getBaseIndex(): Supercluster<{ base: MilitaryBase }> {
  if (baseIndex) return baseIndex;

  baseIndex = new Supercluster<{ base: MilitaryBase }>({
    radius: 60,
    maxZoom: CLUSTER_MAX_ZOOM,
    minPoints: 2,
  });

  baseIndex.load(MILITARY_BASES.map((base) => ({
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: [base.lon, base.lat] },
    properties: { base },
  })));

  return baseIndex;
}

/** Majority faction across a cluster's members. */
export function dominantBaseType(typeCounts: Partial<Record<MilitaryBaseType, number>>): MilitaryBaseType {
  let best: MilitaryBaseType = 'other';
  let bestCount = -1;
  for (const [type, count] of Object.entries(typeCounts)) {
    if ((count ?? 0) > bestCount) {
      best = type as MilitaryBaseType;
      bestCount = count ?? 0;
    }
  }
  return best;
}

/** Query the static index into clusters + unclustered single bases. */
function getBaseClusters(zoom: number): { clusters: BaseCluster[]; singles: MilitaryBase[] } {
  const index = getBaseIndex();
  const features = index.getClusters([-180, -85, 180, 85], Math.floor(zoom));
  const clusters: BaseCluster[] = [];
  const singles: MilitaryBase[] = [];

  for (const f of features) {
    const props = f.properties;
    if ('cluster' in props && props.cluster) {
      const clusterId = (props as unknown as { cluster_id: number }).cluster_id;
      const typeCounts: Partial<Record<MilitaryBaseType, number>> = {};
      for (const leaf of index.getLeaves(clusterId, Infinity)) {
        const type = (leaf.properties as { base: MilitaryBase }).base.type;
        typeCounts[type] = (typeCounts[type] ?? 0) + 1;
      }
      clusters.push({
        lng: f.geometry.coordinates[0],
        lat: f.geometry.coordinates[1],
        count: (props as unknown as { point_count: number }).point_count,
        dominantType: dominantBaseType(typeCounts),
      });
    } else {
      singles.push((props as { base: MilitaryBase }).base);
    }
  }

  return { clusters, singles };
}

// ---------------------------------------------------------------------------
// Layer factory
// ---------------------------------------------------------------------------

function createBaseIconLayer(bases: MilitaryBase[], alpha: number): Layer {
  return new IconLayer<MilitaryBase>({
    id: 'bases-layer',
    data: bases,
    getPosition: (d) => [d.lon, d.lat],
    getIcon: () => 'triangleUp',
    iconAtlas: MARKER_ICONS.triangleUp,
    iconMapping: BASES_ICON_MAPPING,
    // WM icon footprint (DeckGLMap.ts:2952, 2959-2961) -- the earlier
    // 14/10-22px variant crowded dense base fields into a solid mass
    getSize: 11,
    getColor: (d) => getBaseColor(d.type, alpha),
    sizeScale: 1,
    sizeMinPixels: 6,
    sizeMaxPixels: 16,
    pickable: true,
  });
}

/** Create deck.gl layers for military base markers -- triangle icons, country-colored. */
export function createMilitaryBaseLayers(
  _events: unknown[],
  zoom: number,
): Layer[] {
  const alpha = Math.round(160 * Math.max(0.3, Math.min(1, (zoom - 2.5) / 2.5)));

  if (zoom >= CLUSTER_MAX_ZOOM) {
    return [createBaseIconLayer(MILITARY_BASES, alpha)];
  }

  // Cluster band: faction-colored discs (log2 radius, WM DeckGLMap.ts:2972-2993)
  // with singles still drawn as triangles
  const { clusters, singles } = getBaseClusters(zoom);
  const clusterAlpha = Math.round(180 * Math.max(0.3, Math.min(1, (zoom - 2.5) / 2.5)));
  const layers: Layer[] = [];

  if (singles.length > 0) {
    layers.push(createBaseIconLayer(singles, alpha));
  }

  if (clusters.length > 0) {
    layers.push(new ScatterplotLayer<BaseCluster>({
      id: 'bases-cluster-layer',
      data: clusters,
      pickable: true,
      filled: true,
      radiusUnits: 'meters' as const,
      getPosition: (d) => [d.lng, d.lat],
      getRadius: (d) => Math.max(8000, Math.log2(d.count) * 6000),
      getFillColor: (d) => getBaseColor(d.dominantType, clusterAlpha),
      radiusMinPixels: 10,
      radiusMaxPixels: 40,
    }));

    // Count centered INSIDE the disc -- deliberately not the offset badge
    // pill used by the protest/datacenter clusters (WM DeckGLMap.ts:2983-2993)
    layers.push(new TextLayer<BaseCluster>({
      id: 'bases-cluster-text',
      data: clusters,
      getPosition: (d) => [d.lng, d.lat],
      getText: (d) => String(d.count),
      getSize: 12,
      getColor: [255, 255, 255, 220],
      fontWeight: 'bold',
      getTextAnchor: 'middle' as const,
      getAlignmentBaseline: 'center' as const,
      sizeUnits: 'pixels' as const,
    }));
  }

  return layers;
}
